// External modules
import { strictIsFinite } from '@checkout/react-components';

// Internal modules
import {
  ACCOUNT_CREATION_OPERATIONS,
  OPERATION_TO_ACCESS_TOKEN_MAP,
} from './constants';

export const getApiCorrelationId = ({ networkError, response }) => {
  const { extensions = {} } = networkError?.result || response || {};

  /**
   * In xobuyer and graphqlnode web we set the corrId on the
   * extensions object as `correlation_id` but graphqlnodeweb
   * also uses a plugin that sets it as `correlationId`.
   * So sometimes one is present and sometimes the other is...
   */
  const { correlationId, correlation_id } = extensions;

  return correlation_id || correlationId || 'NOT_AVAILABLE';
};

export const isOnboardGuest = opName => {
  return opName === ACCOUNT_CREATION_OPERATIONS.ONBOARD_GUEST;
};

export const isSignUpNewMember = opName => {
  return opName === ACCOUNT_CREATION_OPERATIONS.SIGN_UP_NEW_MEMBER;
};

export const isAccountCreationMutation = opName => {
  return isOnboardGuest(opName) || isSignUpNewMember(opName);
};

/**
 * @function getAccessTokenType
 * @param {string} opName
 * @returns {string}
 * @description Takes an operation name
 */
export const getAccessTokenType = opName => {
  return OPERATION_TO_ACCESS_TOKEN_MAP[opName] || null;
};

/**
 * @function mapMutationNameToDataNamespace
 * @param {string} opName
 * @returns {string}
 * @description Takes the name of a mutation, e.g. `SignUpNewMemberMutation`,
 * and maps it to a the namespace that the data queried for is under,
 * e.g. `signUpNewMember`.
 */
export const mapMutationNameToDataNamespace = name => {
  return name
    .replace(/Mutation$/, '')
    .replace(/^(\w)/, (_, first) => first?.toLowerCase());
};

/**
 * Creating buckets of 500ms for API call durations with a max
 * bucket of 11,500-11,999.
 *
 * Array(n) returns an array of length n but with empty values.
 * This means Array(n).map() won't work. Array#fill() isn't available
 * in IE 11 and it's not worth polyfilling for this.
 */
const DURATION_BUCKETS = [...Array(24)].map((_, i) => {
  return `${i * 500}-${i * 500 + 499}`;
});
DURATION_BUCKETS.OVERFLOW = '12000+';

export const getDurationBucket = n => {
  // Seeing a few instances of API calls that have negative duration...
  // (╯°□°）╯︵ ┻━┻
  if (!strictIsFinite(n) || n < 0) {
    return null;
  }

  const index = Math.floor(n / 500);
  return DURATION_BUCKETS[index] || DURATION_BUCKETS.OVERFLOW;
};

const getErrorFromResponse = response => {
  const { graphQLErrors, networkError } = response;
  const firstGQLError = graphQLErrors?.[0];
  const firstNetworkError = networkError?.result?.errors?.[0];

  return firstGQLError || firstNetworkError || response;
};

export const requestFactory = (client, operationType) => {
  return (...args) => {
    return client[operationType](...args).catch(response => {
      const error = getErrorFromResponse(response);
      throw error;
    });
  };
};
